import * as React from 'react';
import { clsx } from 'clsx';

type PaddingVariant = 'none' | 'small' | 'medium' | 'large';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The padding variant of the card
   * @default 'large'
   */
  padding?: PaddingVariant;
  /**
   * The content of the card
   */
  children: React.ReactNode;
}

// Arbor Design System card styles
const cardStyles = {
  base: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #efefef', // grey-100 - subtle border
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  padding: {
    none: {
      padding: '0',
    },
    small: {
      padding: '8px', // small spacing
    },
    medium: {
      padding: '16px', // large spacing
    },
    large: {
      padding: '24px', // xlarge spacing
    },
  },
};

/**
 * Card component - Arbor Design System
 *
 * A container component that provides consistent styling for content cards.
 * Uses subtle borders rather than shadows, following Arbor's minimal design approach.
 *
 * @example
 * ```tsx
 * <Card padding="large">
 *   <h2>Card Title</h2>
 *   <p>Card content goes here</p>
 * </Card>
 * ```
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ padding = 'large', className, style, children, ...props }, ref) => {
    const paddingStyles = cardStyles.padding[padding];

    const combinedStyle: React.CSSProperties = {
      ...cardStyles.base,
      ...paddingStyles,
      ...style,
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-card', className)}
        style={combinedStyle}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';
